export const TIME_BUCKET_KEYS = [
    'before10Min', 'before20Min', 'before30Min', 'before40Min',
    'before50Min', 'before60Min', 'after60Min'
];

export const TIME_BUCKET_LABELS = [
    '0-10 min', '10-20 min', '20-30 min', '30-40 min',
    '40-50 min', '50-60 min', '60+ min'
];

export const LATENESS_EARLY_KEYS = [
    'early45Min', 'early30Min', 'early15Min', 'early10Min', 'early5Min', 'early0Min'
];

export const LATENESS_LATE_KEYS = [
    'late5Min', 'late10Min', 'late15Min', 'late30Min', 'late45Min', 'lateOver45Min'
];

export const LATENESS_LABELS = [
    'ontime', '1 - 5', '5 - 10', '10 - 15', '15 - 30', '30 - 45', '45+'
];

/**
 * Lateness bar labels, adjusted for an on-time grace buffer. When buffered, the
 * "ontime" bar covers deliveries up to `onTimeBufferMinutes` late, so the first
 * late bucket becomes (buffer, 5] and is relabeled "{buffer+1} - 5" (e.g. "4 - 5"
 * for a 3-min buffer). buffer 0 returns the default labels unchanged.
 * Assumes buffer < 5 (holds for the current 3-min case).
 */
export function latenessLabels(onTimeBufferMinutes = 0) {
    if (!onTimeBufferMinutes) return LATENESS_LABELS;
    const labels = [...LATENESS_LABELS];
    labels[1] = `${onTimeBufferMinutes + 1} - 5`;
    return labels;
}

export const CHART_TYPES = {
    PICKUP: 'pickup',
    DELIVERY: 'delivery',
    DRIVE: 'drive',
    LATENESS: 'lateness'
};

export const DURATION_BUCKET_ENUMS = [
    'BEFORE_10_MIN', 'BEFORE_20_MIN', 'BEFORE_30_MIN', 'BEFORE_40_MIN',
    'BEFORE_50_MIN', 'BEFORE_60_MIN', 'AFTER_60_MIN',
];

export const LATENESS_BUCKET_ENUMS = [
    'ON_TIME', 'LATE_5_MIN', 'LATE_10_MIN', 'LATE_15_MIN',
    'LATE_30_MIN', 'LATE_45_MIN', 'LATE_OVER_45_MIN',
];

export function mapBucketIndexToEnum(chartType, bucketIndex) {
    if (chartType === CHART_TYPES.LATENESS) {
        return LATENESS_BUCKET_ENUMS[bucketIndex] ?? null;
    }
    return DURATION_BUCKET_ENUMS[bucketIndex] ?? null;
}

export function getBucketLabel(chartType, bucketIndex, onTimeBufferMinutes = 0) {
    if (chartType === CHART_TYPES.LATENESS) return latenessLabels(onTimeBufferMinutes)[bucketIndex] ?? '';
    return TIME_BUCKET_LABELS[bucketIndex] ?? '';
}

// Backend Timeframe enum uses the label value directly (today, thisWeek, lastMonth, …)
// so the frontend TIMEFRAME_OPTIONS values are already the correct strings to send.
export function mapTimeframeLabelToApi(label) {
    return label;
}

export const TIMEFRAME_OPTIONS_MAP = {
    today: "Today",
    yesterday: "Yesterday",
    thisWeek: "This Week",
    lastWeek: "Last Week",
    thisMonth: "This Month",
    lastMonth: "Last Month",
};

const CHART_TYPE_TO_API = {
    [CHART_TYPES.PICKUP]: 'PICKUP',
    [CHART_TYPES.DELIVERY]: 'DELIVERY',
    [CHART_TYPES.DRIVE]: 'DRIVE',
};

export function mapChartTypeToApi(chartType) {
    return CHART_TYPE_TO_API[chartType] ?? null;
}

/**
 * Build per-company chart map from the 4 RTK Query response models.
 * Input: { pickup: ChPerformanceChartResponse, delivery, drive, lateness }
 * Output: { [companyId]: { pickup: {distinct, cumulative}, delivery: {...}, drive: {...}, lateness: {...} } }
 */
export function buildCompanyChartMap({ pickup, delivery, drive, lateness }) {
    const result = {};
    const merge = (chartType, response) => {
        if (!response) return;
        for (const c of response.getCompanies()) {
            if (!result[c.companyId]) result[c.companyId] = {};
            result[c.companyId][chartType] = {
                distinct: c.getDistinct(),
                cumulative: c.getCumulative(),
            };
        }
    };
    merge(CHART_TYPES.PICKUP, pickup);
    merge(CHART_TYPES.DELIVERY, delivery);
    merge(CHART_TYPES.DRIVE, drive);
    merge(CHART_TYPES.LATENESS, lateness);
    return result;
}

/**
 * Build per-company summary map from the summary response model.
 * Output: { [companyId]: ChPerformanceCompanySummary }
 */
export function buildCompanySummaryMap(summaryResponse) {
    const result = {};
    if (!summaryResponse) return result;
    for (const c of summaryResponse.getCompanies()) {
        result[c.companyId] = c;
    }
    return result;
}

/**
 * Aggregate bucket data across selected companies (weighted by completedDeliveries).
 * Returns: { barData: number[], lineData: number[] }
 */
export function aggregateChartBuckets(companyCharts, chartType, companySummaries, companyIdArr = null) {
    try {
        const targetCompanies = companyIdArr
            ? companyIdArr.map(String)
            : Object.keys(companyCharts);

        const isLateness = chartType === CHART_TYPES.LATENESS;
        const bucketCount = 7;

        let totalOrders = 0;
        const weightedDistinct = new Array(bucketCount).fill(0);
        const weightedCumulative = new Array(bucketCount).fill(0);

        targetCompanies.forEach(companyId => {
            const chartData = companyCharts[companyId]?.[chartType];
            const summary = companySummaries[companyId];
            if (!chartData || !summary) return;

            const orders = summary.completedDeliveries || 0;
            if (orders === 0) return;

            const distinctObj = Array.isArray(chartData.distinct) ? chartData.distinct[0] : chartData.distinct;
            const cumulativeObj = Array.isArray(chartData.cumulative) ? chartData.cumulative[0] : chartData.cumulative;
            if (!distinctObj && !cumulativeObj) return;

            totalOrders += orders;

            const distinctArr = isLateness
                ? extractLatenessBuckets(distinctObj, false)
                : extractTimeBuckets(distinctObj);
            const cumulativeArr = isLateness
                ? extractLatenessBuckets(cumulativeObj, true)
                : extractTimeBuckets(cumulativeObj);

            for (let i = 0; i < bucketCount; i++) {
                weightedDistinct[i] += (distinctArr[i] || 0) * orders;
                weightedCumulative[i] += (cumulativeArr[i] || 0) * orders;
            }
        });

        if (totalOrders === 0) {
            return {
                barData: new Array(bucketCount).fill(0),
                lineData: new Array(bucketCount).fill(0)
            };
        }

        return {
            barData: weightedDistinct.map(v => v / totalOrders),
            lineData: weightedCumulative.map(v => v / totalOrders)
        };
    } catch (e) {
        console.error('aggregateChartBuckets error:', e);
        return {
            barData: new Array(7).fill(0),
            lineData: new Array(7).fill(0)
        };
    }
}

function extractTimeBuckets(obj) {
    if (!obj) return new Array(7).fill(0);
    return TIME_BUCKET_KEYS.map(key => parseFloat(obj[key]) || 0);
}

function extractLatenessBuckets(obj, isCumulative) {
    if (!obj) return new Array(7).fill(0);

    let ontimeValue = 0;
    if (isCumulative) {
        ontimeValue = parseFloat(obj['early0Min']) || 0;
    } else {
        LATENESS_EARLY_KEYS.forEach(key => {
            ontimeValue += parseFloat(obj[key]) || 0;
        });
    }

    const result = [ontimeValue];
    LATENESS_LATE_KEYS.forEach(key => {
        result.push(parseFloat(obj[key]) || 0);
    });

    return result;
}

/**
 * Generate breakdown table rows from the summary response.
 * Mirrors generatePerformanceTable from src/Util/TableHelpers/performanceTableHelper.js
 * but reads aggregated fields directly off ChPerformanceCompanySummary (no per-day reduction).
 */
export function generateChPerformanceTable(accountsMap, companySummaryMap, selectedCompanyIds) {
    const ids = selectedCompanyIds ?? [];

    return ids
        .filter(id => companySummaryMap[id])
        .map(id => {
            const c = companySummaryMap[id];
            const name = accountsMap?.[id]?.companyName ?? `Company ${id}`;
            return [
                name,
                valueSanitization(c.completedDeliveries, false),
                valueSanitization(c.onTimeDeliveries),
                inMinutes(c.avgPromiseTime),
                inMinutes(c.avgPlacementToPickupTime),
                inMinutes(c.avgPickupToDeliveryTime),
                inMinutes(c.avgPlacementToDeliveryTime),
                percentFormat(c.pickupBefore20Min),
                percentFormat(c.deliveryBefore40Min),
            ];
        });
}

function inMinutes(val) {
    val = Number(val);
    if (val === -1 || isNaN(val) || val === 0) return '0 min';
    return Math.round(val) + ' min';
}

function valueSanitization(val, isFloat = true) {
    if (val === -1 || isNaN(val) || val === undefined || val === null) return 'N/A';
    if (isFloat) return Number(val).toFixed(2);
    return val;
}

function percentFormat(val) {
    val = Number(val);
    if (isNaN(val)) return 'N/A';
    return Number(val).toFixed(2) + '%';
}
